// src/pages/Users.js
import React, { useEffect, useState } from 'react';
import { Container, Table, Form, Button, Pagination, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, deleteUser } from '../redux/actions/userActions';
import { Link } from 'react-router-dom';

function Users() {
  const dispatch = useDispatch();
  // Retrieve the full users list and totalPages from Redux
  const { users, totalPages } = useSelector((state) => state.user);
  // Retrieve the logged-in user's id
  const authUserId = useSelector((state) => state.auth.user?.id);
  
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // State for the delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Initially, fetch users. The backend returns all users regardless of search.
  useEffect(() => {
    dispatch(fetchUsers({ search: '', page }));
  }, [dispatch, page]);

  // Compute filtered users based on the search term (front-end filtering)
  const filteredUsers = users.filter((user) => 
    user.name.toLowerCase().includes(search.toLowerCase()) ||
    user.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleSearch = (e) => {
    e.preventDefault();
    // No need to dispatch fetchUsers with a search term, since we filter locally.
    // Optionally, you might want to reset the pagination.
    setPage(1);
  };

  // Delete modal handling
  const handleDeleteClick = (id) => {
    setUserToDelete(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      dispatch(deleteUser(userToDelete)).then(() => {
        // After deletion, refresh the user list.
        dispatch(fetchUsers({ search: '', page }));
      });
    }
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
    // Optionally refresh the list if needed.
    dispatch(fetchUsers({ search: '', page }));
  };

  // Build pagination items (assuming totalPages remains valid for the full list)
  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item
        key={number}
        active={number === page}
        onClick={() => setPage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  return (
    <Container className="mt-4">
      <h2>User Management</h2>
      <Form onSubmit={handleSearch} className="mb-3">
        <Form.Group controlId="search">
          <Form.Label>Search by Name or Email</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter search term"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Form.Group>
        <Button variant="primary" type="submit" className="mt-2">
          Search
        </Button>
      </Form>
      <Link to="/users/create">
        <Button variant="primary" type="button" className="mb-3">
          Create User
        </Button>
      </Link>
      <Table striped bordered hover variant="dark">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Permissions</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers && filteredUsers.length > 0 ? (
            [...filteredUsers]
              .sort((a, b) => a.id - b.id)
              .map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>{user.role}</td>
                  <td>
                    {user.permissions &&
                    user.permissions.length > 0 &&
                    user.permissions.some((perm) => perm.trim() !== '')
                      ? user.permissions
                          .filter((perm) => perm.trim() !== '')
                          .map((perm, idx) => (
                            <Button
                              key={idx}
                              variant="outline-info"
                              size="sm"
                              className="me-1 mb-1"
                            >
                              {perm}
                            </Button>
                          ))
                      : (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-1 mb-1"
                        >
                          none
                        </Button>
                      )}
                  </td>
                  <td>
                    <Button as={Link} to={`/users/edit/${user.id}`} variant="info" size="sm">
                      Edit
                    </Button>{' '}
                    {user.id === authUserId ? (
                      <Button variant="danger" size="sm" disabled>
                        Delete
                      </Button>
                    ) : (
                      <Button onClick={() => handleDeleteClick(user.id)} variant="danger" size="sm">
                        Delete
                      </Button>
                    )}
                  </td>
                </tr>
              ))
          ) : (
            <tr>
              <td colSpan="6">No users found.</td>
            </tr>
          )}
        </tbody>
      </Table>
      <Pagination>{paginationItems}</Pagination>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this user? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Users;
